import React, { useState, useEffect, useCallback, useRef } from 'react';
import { confirmAction } from '../../utils/confirm';
import {
    fetchDeviceCapabilities,
    configureDevice,
    readDeviceAttributes,
    readReportingConfig,
    fetchAttributeCache,
    fetchPendingQueue,
    fetchReportingConfigCache,
    fetchUnsupportedAttributes,
    clearUnsupportedAttributes,
    fetchDeviceHealth,
    ZigbeeDeviceHealth,
    ZigbeeCapabilities,
    ZigbeeSettableProp,
    ZigbeeReportableProp,
    PendingQueue,
} from '../../services/api';
import { ui } from '../../styles/shared';

// Stilar från den gemensamma stilfilen
const card = ui.card;
const btn = ui.btn;
const inputStyle = ui.input;
const labelStyle = ui.label;
const hintStyle = ui.hint;

interface Props {
    deviceId: string;
    /** True for battery-powered devices — live reads will never succeed while sleeping. */
    isBattery?: boolean;
}

type WriteValues = Record<string, string>; // property → raw string input

interface ReportingValues {
    min: string;
    max: string;
    change: string;
}
type ReportingForms = Record<string, ReportingValues>; // property → { min, max, change }

const sectionTitle: React.CSSProperties = {
    marginTop: 0,
    marginBottom: '12px',
    fontSize: '16px',
    fontWeight: 700,
};

const propRow: React.CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
    padding: '12px 0',
    borderBottom: '1px solid #f0f0f0',
};

const descStyle: React.CSSProperties = {
    fontSize: '12px',
    color: '#888',
    marginTop: '2px',
};

const statusLabel = (status: 'ok' | 'queued' | 'error'): string =>
    status === 'ok' ? 'klart' : status === 'queued' ? 'i kö' : 'fel';

const statusTag = (status: 'ok' | 'queued' | 'error'): React.CSSProperties => ({
    display: 'inline-block',
    padding: '2px 8px',
    borderRadius: '10px',
    fontSize: '12px',
    fontWeight: 600,
    backgroundColor: status === 'ok' ? '#d4edda' : status === 'queued' ? '#fff3cd' : '#f8d7da',
    color: status === 'ok' ? '#155724' : status === 'queued' ? '#856404' : '#721c24',
});

// ── Settable property row ────────────────────────────────────────────────────

interface SettableRowProps {
    prop: ZigbeeSettableProp;
    value: string;
    onChange: (v: string) => void;
    onApply: (prop: ZigbeeSettableProp, rawValue: string) => void;
    onRead: (prop: ZigbeeSettableProp) => void;
    result: { status: 'ok' | 'queued' | 'error'; error?: string } | null;
    saving: boolean;
    reading: boolean;
    cachedAt?: string; // ISO string — set when value came from attribute cache
    isBattery?: boolean;
}

const SettableRow: React.FC<SettableRowProps> = ({ prop, value, onChange, onApply, onRead, result, saving, reading, cachedAt, isBattery }) => (
    <div style={propRow}>
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '12px' }}>
            <div style={{ flex: 1 }}>
                <label style={{ ...labelStyle, marginBottom: '2px' }}>{prop.property}</label>
                <span style={descStyle}>{prop.description}{prop.unit ? ` (${prop.unit})` : ''}</span>
                <span style={{ ...descStyle, marginTop: '2px' }}>
                    kluster: <code style={{ fontSize: '11px' }}>{prop.cluster}</code> · egenskap: <code style={{ fontSize: '11px' }}>{prop.attribute}</code>
                </span>
                {cachedAt && !result && (
                    <span style={{ ...descStyle, marginTop: '2px', fontStyle: 'italic' }}>
                        {isBattery
                            ? `senast kända värdet · ${new Date(cachedAt).toLocaleString('sv-SE')} — tryck på Ställ i kö för att hämta nytt nästa gång enheten vaknar`
                            : `hämtat ${new Date(cachedAt).toLocaleString('sv-SE')} — tryck på Läs för att hämta nytt`}
                    </span>
                )}
            </div>
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexShrink: 0, marginTop: '4px' }}>
                {prop.type === 'enum' && prop.values ? (
                    <select
                        value={value}
                        onChange={e => onChange(e.target.value)}
                        style={{ ...inputStyle, width: '140px' }}
                    >
                        <option value="">— välj —</option>
                        {prop.values.map(v => (
                            <option key={v.value} value={String(v.value)}>{v.label} ({v.value})</option>
                        ))}
                    </select>
                ) : prop.type === 'boolean' ? (
                    <select
                        value={value}
                        onChange={e => onChange(e.target.value)}
                        style={{ ...inputStyle, width: '120px' }}
                    >
                        <option value="">— välj —</option>
                        <option value="1">ja (1)</option>
                        <option value="0">nej (0)</option>
                    </select>
                ) : (
                    <input
                        type="number"
                        value={value}
                        onChange={e => onChange(e.target.value)}
                        placeholder={prop.min !== undefined ? `${prop.min}–${prop.max}` : 'värde'}
                        min={prop.min}
                        max={prop.max}
                        style={{ ...inputStyle, width: '120px' }}
                    />
                )}
                <button
                    disabled={reading}
                    onClick={() => onRead(prop)}
                    title={isBattery ? 'Enheten går på batteri — avläsningen ställs i kö och görs nästa gång enheten vaknar' : undefined}
                    style={{
                        ...btn(reading ? '#6c757d' : isBattery ? '#6c757d' : '#17a2b8'),
                        padding: '7px 12px',
                        fontSize: '13px',
                        cursor: reading ? 'not-allowed' : 'pointer',
                        whiteSpace: 'nowrap',
                        opacity: isBattery ? 0.75 : 1,
                    }}
                >
                    {reading ? '…' : isBattery ? 'Ställ i kö' : 'Läs'}
                </button>
                <button
                    disabled={saving || value === ''}
                    onClick={() => onApply(prop, value)}
                    style={{
                        ...btn(saving || value === '' ? '#6c757d' : '#007bff'),
                        padding: '7px 14px',
                        fontSize: '13px',
                        cursor: saving || value === '' ? 'not-allowed' : 'pointer',
                        whiteSpace: 'nowrap',
                    }}
                >
                    {saving ? '…' : 'Skicka'}
                </button>
            </div>
        </div>
        {result && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '4px' }}>
                <span style={statusTag(result.status)}>{statusLabel(result.status)}</span>
                {result.status === 'queued' && (
                    <span style={descStyle}>Enheten sover — skickas nästa gång den vaknar.</span>
                )}
                {result.status === 'error' && (
                    <span style={{ ...descStyle, color: '#721c24' }}>{result.error}</span>
                )}
            </div>
        )}
    </div>
);

// ── Reportable property row ──────────────────────────────────────────────────

interface ReportableRowProps {
    prop: ZigbeeReportableProp;
    form: ReportingValues;
    onChange: (field: 'min' | 'max' | 'change', v: string) => void;
    onApply: (prop: ZigbeeReportableProp, form: ReportingValues) => void;
    onRead: (prop: ZigbeeReportableProp) => void;
    result: { status: 'ok' | 'queued' | 'error'; error?: string } | null;
    saving: boolean;
    reading: boolean;
    isBattery?: boolean;
}

const ReportableRow: React.FC<ReportableRowProps> = ({ prop, form, onChange, onApply, onRead, result, saving, reading, isBattery }) => (
    <div style={propRow}>
        <div>
            <label style={{ ...labelStyle, marginBottom: '2px' }}>{prop.property}</label>
            <span style={descStyle}>{prop.description}{prop.unit ? ` (${prop.unit})` : ''}</span>
            <span style={{ ...descStyle, marginTop: '2px' }}>
                kluster: <code style={{ fontSize: '11px' }}>{prop.cluster}</code> · egenskap: <code style={{ fontSize: '11px' }}>{prop.attribute}</code>
            </span>
        </div>
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap', marginTop: '4px' }}>
            <div>
                <label style={{ ...hintStyle, display: 'block', marginBottom: '3px' }}>minst (s)</label>
                <input type="number" min="0" placeholder="t.ex. 10" value={form.min}
                    onChange={e => onChange('min', e.target.value)}
                    style={{ ...inputStyle, width: '90px' }} />
            </div>
            <div>
                <label style={{ ...hintStyle, display: 'block', marginBottom: '3px' }}>högst (s)</label>
                <input type="number" min="0" placeholder="t.ex. 300" value={form.max}
                    onChange={e => onChange('max', e.target.value)}
                    style={{ ...inputStyle, width: '90px' }} />
            </div>
            <div>
                <label style={{ ...hintStyle, display: 'block', marginBottom: '3px' }}>ändring (frivilligt)</label>
                <input type="number" min="0" placeholder="t.ex. 50" value={form.change}
                    onChange={e => onChange('change', e.target.value)}
                    style={{ ...inputStyle, width: '90px' }} />
            </div>
            <button
                disabled={reading}
                onClick={() => onRead(prop)}
                title={isBattery ? 'Enheten går på batteri — avläsningen ställs i kö och görs nästa gång enheten vaknar' : undefined}
                style={{
                    ...btn(reading ? '#6c757d' : isBattery ? '#6c757d' : '#17a2b8'),
                    padding: '7px 12px',
                    fontSize: '13px',
                    alignSelf: 'flex-end',
                    cursor: reading ? 'not-allowed' : 'pointer',
                    opacity: isBattery ? 0.75 : 1,
                }}
            >
                {reading ? '…' : isBattery ? 'Ställ i kö' : 'Läs'}
            </button>
            <button
                disabled={saving || !form.min || !form.max}
                onClick={() => onApply(prop, form)}
                style={{
                    ...btn(saving || !form.min || !form.max ? '#6c757d' : '#007bff'),
                    padding: '7px 14px',
                    fontSize: '13px',
                    alignSelf: 'flex-end',
                    cursor: saving || !form.min || !form.max ? 'not-allowed' : 'pointer',
                }}
            >
                {saving ? '…' : 'Skicka'}
            </button>
        </div>
        {result && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '4px' }}>
                <span style={statusTag(result.status)}>{statusLabel(result.status)}</span>
                {result.status === 'queued' && <span style={descStyle}>Skickas nästa gång enheten vaknar.</span>}
                {result.status === 'error' && <span style={{ ...descStyle, color: '#721c24' }}>{result.error}</span>}
            </div>
        )}
    </div>
);

// ── Main panel ───────────────────────────────────────────────────────────────

const ZigbeeCapabilitiesPanel: React.FC<Props> = ({ deviceId, isBattery = false }) => {
    const [open, setOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [capabilities, setCapabilities] = useState<ZigbeeCapabilities | null>(null);
    const [loadError, setLoadError] = useState<string | null>(null);
    const [unsupported, setUnsupported] = useState<Array<{ cluster: string; attribute: string }>>([]);
    const [clearingUnsupported, setClearingUnsupported] = useState(false);
    const [health, setHealth] = useState<ZigbeeDeviceHealth | null>(null);

    // Per-property write state
    const [writeValues, setWriteValues] = useState<WriteValues>({});
    const [writeResults, setWriteResults] = useState<Record<string, { status: 'ok' | 'queued' | 'error'; error?: string }>>({});
    const [writeSaving, setWriteSaving] = useState<Record<string, boolean>>({});
    const [writeReading, setWriteReading] = useState<Record<string, boolean>>({});
    const [writeCachedAt, setWriteCachedAt] = useState<Record<string, string>>({}); // ISO timestamp from cache

    // Per-property reporting state
    const [reportingForms, setReportingForms] = useState<ReportingForms>({});
    const [reportingResults, setReportingResults] = useState<Record<string, { status: 'ok' | 'queued' | 'error'; error?: string }>>({});
    const [reportingSaving, setReportingSaving] = useState<Record<string, boolean>>({});
    const [reportingReading, setReportingReading] = useState<Record<string, boolean>>({});

    const [activeTab, setActiveTab] = useState<'set' | 'report' | 'clusters'>('set');

    // Pending queue — poll every 10s while panel is open
    const [pendingQueue, setPendingQueue] = useState<PendingQueue>({ reads: [], reportingReads: [], reportingConfigs: [] });
    const pollTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);

    // Refs used inside queue-completion effect to avoid stale closures
    const prevQueueRef = useRef<PendingQueue>({ reads: [], reportingReads: [], reportingConfigs: [] });
    const capabilitiesRef = useRef<ZigbeeCapabilities | null>(null);
    useEffect(() => { capabilitiesRef.current = capabilities; }, [capabilities]);

    const refreshQueue = useCallback(async () => {
        const q = await fetchPendingQueue(deviceId);
        setPendingQueue(q);
    }, [deviceId]);

    useEffect(() => {
        if (!open) {
            if (pollTimerRef.current) clearInterval(pollTimerRef.current);
            return;
        }
        refreshQueue();
        pollTimerRef.current = setInterval(refreshQueue, 10000);
        return () => { if (pollTimerRef.current) clearInterval(pollTimerRef.current); };
    }, [open, refreshQueue]);

    // When queued reads/configs complete (disappear from queue), auto-refresh the cache into the forms
    useEffect(() => {
        const prev = prevQueueRef.current;
        prevQueueRef.current = pendingQueue;
        const caps = capabilitiesRef.current;
        if (!caps) return;

        const prevReportingKeys = new Set(prev.reportingReads.map(r => `${r.cluster}.${r.attribute}`));
        const currReportingKeys = new Set(pendingQueue.reportingReads.map(r => `${r.cluster}.${r.attribute}`));
        const reportingCompleted = [...prevReportingKeys].some(k => !currReportingKeys.has(k));

        if (reportingCompleted) {
            fetchReportingConfigCache(deviceId).then(cache => {
                if (!cache.length) return;
                const rcMap = new Map(cache.map(e => [`${e.cluster}.${e.attribute}`, e]));
                setReportingForms(p => {
                    const next = { ...p };
                    for (const prop of caps.reportableProperties) {
                        const entry = rcMap.get(`${prop.cluster}.${prop.attribute}`);
                        if (entry) {
                            next[prop.property] = {
                                min: entry.min !== undefined ? String(entry.min) : (next[prop.property]?.min ?? ''),
                                max: entry.max !== undefined ? String(entry.max) : (next[prop.property]?.max ?? ''),
                                change: entry.change !== undefined ? String(entry.change) : (next[prop.property]?.change ?? ''),
                            };
                        }
                    }
                    return next;
                });
                // Clear the 'queued' status badge for completed items
                setReportingResults(p => {
                    const next = { ...p };
                    for (const prop of caps.reportableProperties) {
                        const key = `${prop.cluster}.${prop.attribute}`;
                        if (prevReportingKeys.has(key) && !currReportingKeys.has(key)) delete next[prop.property];
                    }
                    return next;
                });
            }).catch(() => {});
        }

        const prevReadKeys = new Set(prev.reads.map(r => `${r.cluster}.${r.attribute}`));
        const currReadKeys = new Set(pendingQueue.reads.map(r => `${r.cluster}.${r.attribute}`));
        const readsCompleted = [...prevReadKeys].some(k => !currReadKeys.has(k));

        if (readsCompleted) {
            fetchAttributeCache(deviceId).then(cached => {
                if (!cached.length) return;
                const cacheMap = new Map(cached.map(e => [`${e.cluster}::${e.attribute}`, e]));
                const newCachedAt: Record<string, string> = {};
                setWriteValues(p => {
                    const next = { ...p };
                    for (const prop of caps.settableProperties) {
                        const entry = cacheMap.get(`${prop.cluster}::${prop.attribute}`);
                        if (entry !== undefined && entry.value !== null && entry.value !== undefined) {
                            next[prop.property] = String(entry.value);
                            newCachedAt[prop.property] = entry.updated_at;
                        }
                    }
                    return next;
                });
                setWriteCachedAt(p => ({ ...p, ...newCachedAt }));
                setWriteResults(p => {
                    const next = { ...p };
                    for (const prop of caps.settableProperties) {
                        const key = `${prop.cluster}.${prop.attribute}`;
                        if (prevReadKeys.has(key) && !currReadKeys.has(key)) delete next[prop.property];
                    }
                    return next;
                });
            }).catch(() => {});
        }
    }, [pendingQueue, deviceId]);

    const load = useCallback(async () => {
        setLoading(true);
        setLoadError(null);
        try {
            const [caps, cached, reportingCache, unsup, hp] = await Promise.all([
                fetchDeviceCapabilities(deviceId),
                fetchAttributeCache(deviceId),
                fetchReportingConfigCache(deviceId),
                fetchUnsupportedAttributes(deviceId),
                fetchDeviceHealth(deviceId),
            ]);
            setCapabilities(caps);
            setUnsupported(unsup ?? []);
            setHealth(hp);
            if (!caps) setLoadError('Enheten finns inte hos samordnaren — kontrollera att den är ansluten.');

            // Pre-fill settable inputs from attribute cache
            if (caps && cached.length > 0) {
                const cacheMap = new Map(cached.map(e => [`${e.cluster}::${e.attribute}`, e]));
                const newCachedAt: Record<string, string> = {};
                setWriteValues(prev => {
                    const next = { ...prev };
                    for (const prop of caps.settableProperties) {
                        const entry = cacheMap.get(`${prop.cluster}::${prop.attribute}`);
                        if (entry !== undefined && entry.value !== null && entry.value !== undefined) {
                            next[prop.property] = String(entry.value);
                            newCachedAt[prop.property] = entry.updated_at;
                        }
                    }
                    return next;
                });
                setWriteCachedAt(prev => ({ ...prev, ...newCachedAt }));
            }

            // Pre-fill reporting forms from in-memory cache (populated after configure/read)
            if (caps && reportingCache.length > 0) {
                const rcMap = new Map(reportingCache.map(e => [`${e.cluster}.${e.attribute}`, e]));
                setReportingForms(prev => {
                    const next = { ...prev };
                    for (const prop of caps.reportableProperties) {
                        const entry = rcMap.get(`${prop.cluster}.${prop.attribute}`);
                        if (entry) {
                            next[prop.property] = {
                                min: entry.min !== undefined ? String(entry.min) : '',
                                max: entry.max !== undefined ? String(entry.max) : '',
                                change: entry.change !== undefined ? String(entry.change) : '',
                            };
                        }
                    }
                    return next;
                });
            }
        } catch (e: any) {
            setLoadError(e.message);
        } finally {
            setLoading(false);
        }
    }, [deviceId]);

    useEffect(() => {
        if (open && !capabilities && !loading) load();
    }, [open, capabilities, loading, load]);

    // Reset all per-device state when this (reused) panel instance is pointed at a different
    // device — e.g. switching which device is being edited. Without this the component keeps the
    // previous device's capabilities/values until it is unmounted and remounted (toggling the
    // surrounding "Zigbee configuration" section). Clearing capabilities re-triggers the load
    // effect above while the panel stays open.
    const isFirstDevice = useRef(true);
    useEffect(() => {
        if (isFirstDevice.current) { isFirstDevice.current = false; return; }
        setCapabilities(null);
        setLoadError(null);
        setUnsupported([]);
        setHealth(null);
        setWriteValues({});
        setWriteResults({});
        setWriteSaving({});
        setWriteReading({});
        setWriteCachedAt({});
        setReportingForms({});
        setReportingResults({});
        setReportingSaving({});
        setReportingReading({});
        setPendingQueue({ reads: [], reportingReads: [], reportingConfigs: [] });
        prevQueueRef.current = { reads: [], reportingReads: [], reportingConfigs: [] };
    }, [deviceId]);

    // Re-fetch attribute cache every time the panel opens (even if capabilities already loaded)
    // so textboxes are always pre-filled with the latest cached values.
    const applyCache = useCallback(async (caps: ZigbeeCapabilities) => {
        try {
            const cached = await fetchAttributeCache(deviceId);
            if (cached.length === 0) return;
            const cacheMap = new Map(cached.map(e => [`${e.cluster}::${e.attribute}`, e]));
            const newCachedAt: Record<string, string> = {};
            setWriteValues(prev => {
                const next = { ...prev };
                for (const prop of caps.settableProperties) {
                    const entry = cacheMap.get(`${prop.cluster}::${prop.attribute}`);
                    if (entry !== undefined && entry.value !== null && entry.value !== undefined) {
                        next[prop.property] = String(entry.value);
                        newCachedAt[prop.property] = entry.updated_at;
                    }
                }
                return next;
            });
            setWriteCachedAt(prev => ({ ...prev, ...newCachedAt }));
        } catch { /* ignore */ }
    }, [deviceId]);

    useEffect(() => {
        if (open && capabilities) applyCache(capabilities);
    }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleApplyWrite = async (prop: ZigbeeSettableProp, rawValue: string) => {
        const numValue = Number(rawValue);
        if (isNaN(numValue)) return;
        setWriteSaving(p => ({ ...p, [prop.property]: true }));
        setWriteResults(p => { const n = { ...p }; delete n[prop.property]; return n; });
        try {
            const result = await configureDevice(deviceId, {
                ...(prop.endpoint !== undefined ? { endpoint: prop.endpoint } : {}),
                write: [{ cluster: prop.cluster, attributes: { [prop.attribute]: numValue } }],
            });
            const r = result.results?.[0];
            setWriteResults(p => ({
                ...p,
                [prop.property]: { status: r?.status ?? 'ok', error: r?.error },
            }));
        } catch (e: any) {
            setWriteResults(p => ({ ...p, [prop.property]: { status: 'error', error: e.message } }));
        } finally {
            setWriteSaving(p => ({ ...p, [prop.property]: false }));
        }
    };

    const handleReadValue = async (prop: ZigbeeSettableProp) => {
        setWriteReading(p => ({ ...p, [prop.property]: true }));
        try {
            const results = await readDeviceAttributes(deviceId, [{
                cluster: prop.cluster,
                attribute: prop.attribute,
                ...(prop.endpoint !== undefined ? { endpoint: prop.endpoint } : {}),
            }]);
            const r = results?.[0];
            if (r && r.status === 'ok' && r.value !== null && r.value !== undefined) {
                setWriteValues(p => ({ ...p, [prop.property]: String(r.value) }));
                // Clear cache hint and any previous result — value is now live
                setWriteCachedAt(p => { const n = { ...p }; delete n[prop.property]; return n; });
                setWriteResults(p => { const n = { ...p }; delete n[prop.property]; return n; });
            } else if (r?.status === 'queued') {
                setWriteResults(p => ({
                    ...p,
                    [prop.property]: { status: 'queued', error: r.error },
                }));
            } else {
                setWriteResults(p => ({
                    ...p,
                    [prop.property]: { status: 'error', error: r?.error ?? 'Inget värde kom tillbaka' },
                }));
            }
        } catch (e: any) {
            setWriteResults(p => ({ ...p, [prop.property]: { status: 'error', error: e.message } }));
        } finally {
            setWriteReading(p => ({ ...p, [prop.property]: false }));
        }
    };

    const handleReadReportingConfig = async (prop: ZigbeeReportableProp) => {
        setReportingReading(p => ({ ...p, [prop.property]: true }));
        try {
            const results = await readReportingConfig(deviceId, [{
                cluster: prop.cluster,
                attribute: prop.attribute,
            }]);
            const r = results?.[0];
            if (r && (r.status === 'ok' || r.status === 'queued') && (r.min !== undefined || r.max !== undefined)) {
                // Fill form with live or last-known cached values
                setReportingForms(p => ({
                    ...p,
                    [prop.property]: {
                        min: r.min !== undefined ? String(r.min) : (p[prop.property]?.min ?? ''),
                        max: r.max !== undefined ? String(r.max) : (p[prop.property]?.max ?? ''),
                        change: r.change !== undefined ? String(r.change) : (p[prop.property]?.change ?? ''),
                    },
                }));
                if (r.status === 'queued') {
                    setReportingResults(p => ({ ...p, [prop.property]: { status: 'queued', error: r.error } }));
                } else {
                    setReportingResults(p => { const n = { ...p }; delete n[prop.property]; return n; });
                }
            } else if (r?.status === 'queued') {
                setReportingResults(p => ({
                    ...p,
                    [prop.property]: { status: 'queued', error: r.error },
                }));
            } else {
                setReportingResults(p => ({
                    ...p,
                    [prop.property]: { status: 'error', error: r?.error ?? 'Ingen inställning kom tillbaka' },
                }));
            }
        } catch (e: any) {
            setReportingResults(p => ({ ...p, [prop.property]: { status: 'error', error: e.message } }));
        } finally {
            setReportingReading(p => ({ ...p, [prop.property]: false }));
        }
    };

    const handleApplyReporting = async (prop: ZigbeeReportableProp, form: ReportingValues) => {
        const min = Number(form.min);
        const max = Number(form.max);
        const change = form.change ? Number(form.change) : undefined;
        if (isNaN(min) || isNaN(max)) return;
        setReportingSaving(p => ({ ...p, [prop.property]: true }));
        setReportingResults(p => { const n = { ...p }; delete n[prop.property]; return n; });
        try {
            const result = await configureDevice(deviceId, {
                reporting: [{ cluster: prop.cluster, attribute: prop.attribute, min, max, ...(change !== undefined ? { change } : {}) }],
            });
            const r = result.results?.[0];
            setReportingResults(p => ({
                ...p,
                [prop.property]: { status: r?.status ?? 'ok', error: r?.error },
            }));
        } catch (e: any) {
            setReportingResults(p => ({ ...p, [prop.property]: { status: 'error', error: e.message } }));
        } finally {
            setReportingSaving(p => ({ ...p, [prop.property]: false }));
        }
    };

    const handleClearUnsupported = async () => {
        if (!confirmAction('Vill du glömma vilka värden enheten sagt nej till? De testas då på nytt nästa gång inställningar skickas.')) return;
        setClearingUnsupported(true);
        try {
            await clearUnsupportedAttributes(deviceId);
            setUnsupported([]);
            await load(); // capabilities will now re-include the cleared attributes
        } catch { /* handled by api layer */ }
        finally { setClearingUnsupported(false); }
    };

    const tabBtn = (tab: typeof activeTab, label: string) => (
        <button
            onClick={() => setActiveTab(tab)}
            style={{
                padding: '6px 16px',
                border: 'none',
                borderBottom: activeTab === tab ? '2px solid #007bff' : '2px solid transparent',
                backgroundColor: 'transparent',
                fontWeight: activeTab === tab ? 700 : 400,
                color: activeTab === tab ? '#007bff' : '#555',
                cursor: 'pointer',
                fontSize: '14px',
            }}
        >
            {label}
        </button>
    );

    return (
        <div style={{ ...card, marginTop: '20px' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <h3 style={{ margin: 0 }}>📡 Vad enheten klarar (Zigbee)</h3>
                <button
                    onClick={() => { setOpen(o => !o); }}
                    style={{
                        ...btn(open ? '#6c757d' : '#007bff'),
                        padding: '6px 14px',
                        fontSize: '13px',
                    }}
                >
                    {open ? 'Dölj' : 'Ändra inställningar'}
                </button>
            </div>

            {open && (
                <div style={{ marginTop: '16px' }}>
                    {loading && <p style={{ color: '#666', fontSize: '14px' }}>Hämtar…</p>}
                    {loadError && (
                        <p style={{ color: '#dc3545', fontSize: '14px' }}>{loadError}</p>
                    )}
                    {!loading && !loadError && capabilities && (
                        <>
                            {/* Device identity — the exact strings used to target this device in the
                                Zigbee Default Settings registry (manufacturer/model overrides). */}
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '20px', padding: '10px 12px', marginBottom: '14px', backgroundColor: '#f1f5f9', border: '1px solid #e2e8f0', borderRadius: '6px' }}>
                                {([['Tillverkare', capabilities.manufacturerName], ['Modell-ID', capabilities.modelId]] as const).map(([label, val]) => (
                                    <div key={label} style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                                        <span style={{ ...hintStyle, fontWeight: 600 }}>{label}</span>
                                        <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                            <code style={{ fontSize: '13px' }}>{val || '—'}</code>
                                            {val && (
                                                <button
                                                    onClick={() => navigator.clipboard?.writeText(val)}
                                                    title="Kopiera"
                                                    style={{ ...btn('#6c757d'), padding: '2px 8px', fontSize: '11px' }}
                                                >
                                                    Kopiera
                                                </button>
                                            )}
                                        </span>
                                    </div>
                                ))}
                            </div>

                            {/* Connectivity health — link quality (recent window) + rejoin count.
                                Low/erratic LQI or a high rejoin count = weak RF link / device resetting. */}
                            {health && (
                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '24px', padding: '10px 12px', marginBottom: '14px', backgroundColor: '#f8f9fa', border: '1px solid #e2e8f0', borderRadius: '6px' }}>
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                                        <span style={{ ...hintStyle, fontWeight: 600 }}>Signalstyrka (senaste {health.lqi.windowHours} tim)</span>
                                        <span style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px' }}>
                                            {(() => {
                                                const v = health.lqi.last;
                                                const c = v == null ? '#888' : v >= 150 ? '#28a745' : v >= 80 ? '#e0a800' : '#dc3545';
                                                return <strong style={{ color: c, fontSize: '15px' }}>{v ?? '—'}</strong>;
                                            })()}
                                            <span style={{ color: '#888' }}>
                                                senaste · snitt {health.lqi.avg ?? '—'} · lägsta {health.lqi.min ?? '—'} · {health.lqi.samples} mätningar
                                            </span>
                                        </span>
                                    </div>
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                                        <span style={{ ...hintStyle, fontWeight: 600 }}>Återanslutningar</span>
                                        <span style={{ fontSize: '13px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                                            <strong style={{ fontSize: '15px', color: health.rejoins.count > 5 ? '#dc3545' : health.rejoins.count > 0 ? '#e0a800' : '#28a745' }}>
                                                {health.rejoins.count}
                                            </strong>
                                            {health.rejoins.lastAt && (
                                                <span style={{ color: '#888' }}>senast {new Date(health.rejoins.lastAt).toLocaleString('sv-SE')}</span>
                                            )}
                                        </span>
                                    </div>
                                </div>
                            )}

                            {/* Learned-unsupported attributes — hidden from the tabs above; shown here so
                                an operator can see what the device rejected and re-probe it if needed. */}
                            {unsupported.length > 0 && (
                                <div style={{ padding: '10px 12px', marginBottom: '14px', backgroundColor: '#fff3cd', border: '1px solid #ffe082', borderRadius: '6px' }}>
                                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px', flexWrap: 'wrap' }}>
                                        <div>
                                            <div style={{ fontWeight: 700, fontSize: '13px', color: '#856404', marginBottom: '4px' }}>
                                                ⚠️ Värden enheten inte stöder ({unsupported.length})
                                            </div>
                                            <div style={{ fontSize: '12px', color: '#856404' }}>
                                                Enheten svarade att den inte stöder de här värdena. De döljs ovanför och ställs inte in längre.
                                            </div>
                                            <div style={{ marginTop: '6px', display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                                                {unsupported.map(u => (
                                                    <code key={`${u.cluster}.${u.attribute}`} style={{ fontSize: '11px', background: '#fff', border: '1px solid #ffe082', borderRadius: '4px', padding: '1px 6px' }}>
                                                        {u.cluster}.{u.attribute}
                                                    </code>
                                                ))}
                                            </div>
                                        </div>
                                        <button
                                            onClick={handleClearUnsupported}
                                            disabled={clearingUnsupported}
                                            title="Glöm dem och testa på nytt nästa gång"
                                            style={{ ...btn(clearingUnsupported ? '#6c757d' : '#dc3545'), padding: '6px 12px', fontSize: '13px', whiteSpace: 'nowrap', cursor: clearingUnsupported ? 'not-allowed' : 'pointer' }}
                                        >
                                            {clearingUnsupported ? 'Rensar…' : 'Rensa och testa på nytt'}
                                        </button>
                                    </div>
                                </div>
                            )}

                            {/* Tabs */}
                            <div style={{ display: 'flex', borderBottom: '1px solid #ddd', marginBottom: '16px', gap: '0' }}>
                                {tabBtn('set', `Går att ställa in (${capabilities.settableProperties.length})`)}
                                {tabBtn('report', `Rapportering (${capabilities.reportableProperties.length})`)}
                                {tabBtn('clusters', `Kluster (${capabilities.endpoints.length} st)`)}
                            </div>

                            {/* Settable tab */}
                            {activeTab === 'set' && (
                                <div>
                                    {capabilities.settableProperties.length === 0 && (
                                        <p style={{ color: '#888', fontSize: '14px' }}>Hittade inget som går att ställa in.</p>
                                    )}
                                    <p style={{ fontSize: '13px', color: '#666', margin: '0 0 12px' }}>
                                        Värdet skickas direkt om enheten svarar. Annars ställs det i kö och skickas automatiskt nästa gång enheten hör av sig.
                                    </p>
                                    {capabilities.settableProperties.map(prop => (
                                        <SettableRow
                                            key={prop.property}
                                            prop={prop}
                                            value={writeValues[prop.property] ?? ''}
                                            onChange={v => setWriteValues(p => ({ ...p, [prop.property]: v }))}
                                            onApply={handleApplyWrite}
                                            onRead={handleReadValue}
                                            result={writeResults[prop.property] ?? null}
                                            saving={writeSaving[prop.property] ?? false}
                                            reading={writeReading[prop.property] ?? false}
                                            cachedAt={writeCachedAt[prop.property]}
                                            isBattery={isBattery}
                                        />
                                    ))}
                                </div>
                            )}

                            {/* Reporting tab */}
                            {activeTab === 'report' && (
                                <div>
                                    <p style={sectionTitle}>
                                        Ställ in hur ofta enheten skickar värden (kortast och längst tid mellan rapporter, i sekunder).
                                    </p>
                                    {capabilities.reportableProperties.length === 0 && (
                                        <p style={{ color: '#888', fontSize: '14px' }}>Hittade inget som går att rapportera.</p>
                                    )}
                                    {capabilities.reportableProperties.map(prop => (
                                        <ReportableRow
                                            key={prop.property}
                                            prop={prop}
                                            form={reportingForms[prop.property] ?? { min: '', max: '', change: '' }}
                                            onChange={(field, v) => setReportingForms(p => ({
                                                ...p,
                                                [prop.property]: { ...(p[prop.property] ?? { min: '', max: '', change: '' }), [field]: v },
                                            }))}
                                            onApply={handleApplyReporting}
                                            onRead={handleReadReportingConfig}
                                            result={reportingResults[prop.property] ?? null}
                                            saving={reportingSaving[prop.property] ?? false}
                                            reading={reportingReading[prop.property] ?? false}
                                            isBattery={isBattery}
                                        />
                                    ))}
                                </div>
                            )}

                            {/* Clusters tab */}
                            {activeTab === 'clusters' && (
                                <div>
                                    {capabilities.endpoints.map(ep => (
                                        <div key={ep.id} style={{ marginBottom: '14px' }}>
                                            <div style={{ fontWeight: 700, marginBottom: '6px' }}>Slutpunkt {ep.id}</div>
                                            <div style={{ display: 'flex', gap: '24px', flexWrap: 'wrap' }}>
                                                <div>
                                                    <div style={{ ...hintStyle, marginBottom: '4px', fontWeight: 600 }}>Ingående kluster</div>
                                                    {ep.inputClusters.length === 0
                                                        ? <span style={hintStyle}>inga</span>
                                                        : ep.inputClusters.map(c => (
                                                            <div key={c} style={{ fontSize: '13px', fontFamily: 'monospace' }}>{c}</div>
                                                        ))}
                                                </div>
                                                <div>
                                                    <div style={{ ...hintStyle, marginBottom: '4px', fontWeight: 600 }}>Utgående kluster</div>
                                                    {ep.outputClusters.length === 0
                                                        ? <span style={hintStyle}>inga</span>
                                                        : ep.outputClusters.map(c => (
                                                            <div key={c} style={{ fontSize: '13px', fontFamily: 'monospace' }}>{c}</div>
                                                        ))}
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}

                            {/* Pending queue */}
                            {(pendingQueue.reads.length > 0 || pendingQueue.reportingReads.length > 0 || pendingQueue.reportingConfigs.length > 0) && (
                                <div style={{ marginTop: '16px', padding: '10px 14px', backgroundColor: '#fff8e1', border: '1px solid #ffe082', borderRadius: '6px' }}>
                                    <div style={{ fontWeight: 700, fontSize: '13px', marginBottom: '8px', color: '#856404' }}>
                                        ⏳ I kö — körs nästa gång enheten vaknar
                                    </div>
                                    {pendingQueue.reads.length > 0 && (
                                        <div style={{ marginBottom: '6px' }}>
                                            <div style={{ fontSize: '12px', fontWeight: 600, color: '#555', marginBottom: '4px' }}>Avläsningar</div>
                                            {pendingQueue.reads.map((r, i) => (
                                                <div key={i} style={{ fontSize: '12px', fontFamily: 'monospace', color: '#333' }}>
                                                    {r.cluster} → {r.attribute} (ep {r.endpointNumber})
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                    {pendingQueue.reportingReads.length > 0 && (
                                        <div style={{ marginBottom: '6px' }}>
                                            <div style={{ fontSize: '12px', fontWeight: 600, color: '#555', marginBottom: '4px' }}>Avläsning av rapportinställningar</div>
                                            {pendingQueue.reportingReads.map((r, i) => (
                                                <div key={i} style={{ fontSize: '12px', fontFamily: 'monospace', color: '#333' }}>
                                                    {r.cluster} → {r.attribute} (ep {r.endpointNumber})
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                    {pendingQueue.reportingConfigs.length > 0 && (
                                        <div>
                                            <div style={{ fontSize: '12px', fontWeight: 600, color: '#555', marginBottom: '4px' }}>Ändring av rapportinställningar</div>
                                            {pendingQueue.reportingConfigs.map((r, i) => (
                                                <div key={i} style={{ fontSize: '12px', fontFamily: 'monospace', color: '#333' }}>
                                                    {r.cluster} → {r.attribute} (ep {r.endpointNumber}) · min={r.min}s max={r.max}s change={r.change}
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            )}

                            <div style={{ marginTop: '14px' }}>
                                <button
                                    onClick={load}
                                    disabled={loading}
                                    style={{ ...btn('#6c757d'), padding: '6px 12px', fontSize: '13px', cursor: loading ? 'not-allowed' : 'pointer' }}
                                >
                                    {loading ? 'Uppdaterar…' : '↻ Uppdatera'}
                                </button>
                            </div>
                        </>
                    )}
                </div>
            )}
        </div>
    );
};

export default ZigbeeCapabilitiesPanel;
